import { clsx } from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";

/**
 * Buttons.
 *
 * Five variants, and no sixth. Every one of them is a rectangle with a small
 * radius and a real border in the outline case — no gradients, no glow, no
 * elevation. A control should read as pressable because of its shape and its
 * contrast, not because it is glowing.
 */

type Variant = "primary" | "secondary" | "ghost" | "quiet" | "danger";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-accent text-on-accent shadow-[0_12px_28px_-20px_var(--ex-accent)] hover:bg-accent-hover active:translate-y-px",
  secondary:
    "bg-surface/80 text-ink border border-line hover:border-line-strong hover:bg-surface-raised active:bg-bg-sunken",
  ghost: "text-ink-muted hover:text-ink hover:bg-surface-muted active:bg-bg-sunken",
  quiet: "text-ink-faint hover:text-ink hover:bg-surface-muted",
  danger:
    "bg-critical-soft text-critical border border-critical/25 hover:border-critical/45 hover:bg-critical-soft",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-xs gap-1.5 rounded-md",
  md: "h-10 px-4 text-sm gap-2 rounded-lg",
  lg: "h-12 px-5 text-base gap-2 rounded-xl",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  trailing?: ReactNode;
  loading?: boolean;
  full?: boolean;
}

/**
 * The shared visual contract for an action-shaped element.
 *
 * Most actions are native buttons. A destination must remain a real link,
 * though, and wrapping a button in an anchor creates two nested interactive
 * controls. Route links use this helper to keep one semantic element without
 * copying the design-system classes into a feature.
 */
export function buttonClassName({
  variant = "secondary",
  size = "md",
  full = false,
  className,
}: Pick<ButtonProps, "variant" | "size" | "full" | "className"> = {}) {
  return clsx(
    "cx-button cx-control inline-flex select-none items-center justify-center whitespace-nowrap font-medium",
    "transition-[border-color,box-shadow,transform] duration-150",
    "disabled:cursor-not-allowed",
    VARIANTS[variant],
    SIZES[size],
    full && "w-full",
    className,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    icon,
    trailing,
    loading = false,
    full = false,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClassName({ variant, size, full, className })}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {trailing}
    </button>
  );
});

/** A square button whose whole content is one icon. Always needs a label. */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: Variant;
  size?: Size;
  active?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = "ghost", size = "md", active, className, children, ...rest },
  ref,
) {
  const box =
    size === "sm"
      ? "size-8 rounded-md"
      : size === "lg"
        ? "size-11 rounded-lg"
        : "size-10 rounded-lg";
  return (
    <button
      ref={ref}
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={rest["aria-pressed"] ?? (active === undefined ? undefined : active)}
      className={clsx(
        "cx-button cx-control inline-flex shrink-0 items-center justify-center transition-[border-color,box-shadow,transform] duration-150",
        "disabled:cursor-not-allowed",
        VARIANTS[variant],
        box,
        active === true && "bg-accent-soft text-accent-ink",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={clsx("size-4 animate-spin", className)}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14.5 8A6.5 6.5 0 0 0 8 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * A segmented control: two to five mutually exclusive options.
 *
 * Implemented as a radio group rather than buttons, so arrow keys move between
 * options the way a keyboard user expects and the current choice is announced
 * as one.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = "md",
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string; icon?: ReactNode }>;
  size?: "sm" | "md";
  label: string;
  className?: string;
}) {
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-orientation="horizontal"
      onKeyDown={(event) => {
        let nextIndex: number | null = null;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          nextIndex = (selectedIndex + 1) % options.length;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          nextIndex = (selectedIndex - 1 + options.length) % options.length;
        } else if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = options.length - 1;
        }
        if (nextIndex === null) return;
        const next = options[nextIndex];
        if (!next) return;
        event.preventDefault();
        onChange(next.value);
        const group = event.currentTarget;
        requestAnimationFrame(() =>
          group.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus(),
        );
      }}
      className={clsx(
        "inline-flex items-center gap-0.5 rounded-lg border border-line-soft bg-bg-sunken/75 p-1",
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            // The documented ARIA pattern for a segmented control. A real
            // radio input cannot carry the icon-and-label content.
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={clsx(
              "cx-control inline-flex items-center gap-1.5 rounded-[0.3rem] font-medium transition-colors duration-150 focus-visible:!shadow-[var(--ex-ring)]",
              size === "sm" ? "h-7 px-2.5 text-2xs" : "h-8 px-3 text-xs",
              selected
                ? "bg-surface-raised text-ink shadow-[var(--ex-shadow-raised)]"
                : "text-ink-faint hover:text-ink-muted",
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
